import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema, type FieldMask } from "@bufbuild/protobuf/wkt";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { LegalService, PolicySchema, type Policy, type PolicyFilter } from "#gen/legal/v1/legal_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { AssetState } from "#lib/api/bundles.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface PolicyList {
	policies: Policy[];
	pageInfo?: PaginationPageInfo;
}

export class LegalClient {
	private client: Client<typeof LegalService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.legal || "/", LegalService, options);
	}

	async listPolicies(orgId: string, filter: PolicyFilter, pagination?: PaginationArguments, options?: CallOptions): Promise<PolicyList> {
		try {
			return await this.client.listPolicies({ orgId, filter, pagination }, options);
		} catch (e) {
			throw handleError(e);
		}
	}

	async createPolicy(policy: Policy): Promise<Policy> {
		try {
			const resp = await this.client.createPolicy({
				orgId: policy.orgId,
				workspaceId: policy.workspaceId,
				name: policy.name,
				description: policy.description,
				folderPath: policy.folderPath,
				content: policy.content,
			});
			return resp.policy as Policy;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updatePolicy(policy: Policy, mask: FieldMask): Promise<Policy> {
		try {
			const resp = await this.client.updatePolicy({
				policy: policy,
				mask: mask,
			});
			return resp.policy!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archivePolicy(id: string): Promise<Policy> {
		try {
			const resp = await this.client.updatePolicy({
				policy: create(PolicySchema, {
					id,
					state: AssetState.ARCHIVED,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return resp.policy!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deletePolicy(id: string): Promise<void> {
		try {
			await this.client.deletePolicy({ id });
		} catch (e) {
			throw handleError(e);
		}
	}

	async restorePolicy(id: string): Promise<Policy> {
		try {
			const res = await this.client.updatePolicy({
				policy: create(PolicySchema, {
					id,
					state: AssetState.ACTIVE,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
			return res.policy!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getPolicy(id: string, version: string): Promise<Policy> {
		try {
			const resp = await this.client.getPolicy({ id, version });
			return resp.policy as Policy;
		} catch (e) {
			throw handleError(e);
		}
	}
}
